import { useState, useEffect, useMemo } from 'react'
import { classes } from '@/api/client'
import type { ClassInfo } from '@/api/client'
import { useSort, sortRows, SortTh, type SortDir, type SortValue } from './sortable'

type RosterSortKey = 'name' | 'code' | 'grade' | 'xp' | 'level' | 'battles' | 'last'
const ROSTER_COLUMNS: { key: RosterSortKey; label: string; first: SortDir; get: (s: any) => SortValue }[] = [
  { key: 'name',    label: 'Student Name', first: 'asc',  get: s => s.display_name },
  { key: 'code',    label: 'Code',         first: 'asc',  get: s => s.student_code },
  { key: 'grade',   label: 'Grade',        first: 'desc', get: s => s.grade },
  { key: 'xp',      label: 'XP',           first: 'desc', get: s => s.xp },
  { key: 'level',   label: 'Level',        first: 'desc', get: s => s.level },
  { key: 'battles', label: 'Battles',      first: 'desc', get: s => s.attempt_count },
  { key: 'last',    label: 'Last Active',  first: 'desc', get: s => s.last_active },
]

export default function RosterManager() {
  const [classList, setClassList] = useState<(ClassInfo & { student_count: number; class_code: string })[]>([])
  const [selectedClass, setSelectedClass] = useState<number | null>(null)
  const [students, setStudents] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // New class form
  const [showNewClass, setShowNewClass] = useState(false)
  const [newName, setNewName]           = useState('')
  const [newGrade, setNewGrade]         = useState(8)
  const [newSection, setNewSection]     = useState('')
  const [newSession, setNewSession]     = useState('pre_2026')

  // Bulk import
  const [rosterText, setRosterText]     = useState('')
  const [importMsg, setImportMsg]       = useState('')

  // Student table sort (default: name A→Z)
  const rosterSort = useSort<RosterSortKey>('name', 'asc')
  const sortedStudents = useMemo(() => {
    const col = ROSTER_COLUMNS.find(c => c.key === rosterSort.key)
    return sortRows(students, col ? col.get : null, rosterSort.dir, s => s.display_name)
  }, [students, rosterSort.key, rosterSort.dir])

  // Join-link copy feedback (class id whose link was just copied)
  const [copiedId, setCopiedId]         = useState<number | null>(null)

  // Roster lock: once names are imported, stop auto-creating unknown ones.
  const [lockBusy, setLockBusy] = useState(false)
  async function toggleRosterLock(cls: { id: number; roster_locked?: number }) {
    setLockBusy(true)
    setError('')
    try {
      const updated = await classes.update(cls.id, { roster_locked: cls.roster_locked ? 0 : 1 })
      setClassList(list => list.map(c => (c.id === cls.id ? { ...c, roster_locked: updated.roster_locked } : c)))
    } catch (err: any) {
      setError(err.message || 'Could not update roster lock')
    } finally {
      setLockBusy(false)
    }
  }

  function joinLink(code: string) {
    return `${window.location.origin}/?class=${encodeURIComponent(code)}`
  }
  async function copyJoinLink(e: React.MouseEvent, cls: { id: number; class_code: string }) {
    e.stopPropagation() // don't toggle the class card
    const link = joinLink(cls.class_code)
    try {
      await navigator.clipboard.writeText(link)
    } catch {
      // Clipboard API can be unavailable (http, old browsers) — fall back to a prompt the user can copy from.
      window.prompt('Copy this join link:', link)
    }
    setCopiedId(cls.id)
    window.setTimeout(() => setCopiedId(c => (c === cls.id ? null : c)), 1800)
  }

  useEffect(() => {
    classes.list().then(setClassList).catch(err => setError(err.message || 'Failed to load classes'))
  }, [])

  useEffect(() => {
    if (!selectedClass) return
    setLoading(true)
    classes.students(selectedClass)
      .then(setStudents)
      .catch(err => setError(err.message || 'Failed to load students'))
      .finally(() => setLoading(false))
  }, [selectedClass])

  async function createClass(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    try {
      await classes.create({ name: newName, grade: newGrade, section: newSection, session_label: newSession })
      const updated = await classes.list()
      setClassList(updated)
      setShowNewClass(false)
      setNewName(''); setNewSection(''); setNewGrade(8); setNewSession('pre_2026')
    } catch (err: any) {
      setError(err.message || 'Failed to create class')
    }
  }

  async function handleBulkImport() {
    if (!selectedClass || !rosterText.trim()) return
    const lines = rosterText.trim().split(/\r?\n/).filter(l => l.trim())
    const studentList = lines
      // Skip a CSV header row if present (from the downloaded template)
      .filter(l => l.split(',')[0].trim().toLowerCase() !== 'display_name')
      .map(l => {
        const parts = l.split(',')
        return { display_name: parts[0].trim(), grade: parts[1] ? Number(parts[1].trim()) : undefined }
      })
    if (studentList.length === 0) {
      setImportMsg('❌ No names found — paste names or upload a CSV')
      return
    }
    try {
      const result = await classes.importRoster(selectedClass, studentList)
      const skipped = result.skipped?.length
        ? ` · skipped ${result.skipped.length} invalid name${result.skipped.length !== 1 ? 's' : ''}`
        : ''
      setImportMsg(`✅ Imported ${result.inserted} of ${result.total} students${skipped}`)
      const updated = await classes.students(selectedClass)
      setStudents(updated)
      setRosterText('')
    } catch (err: any) {
      setImportMsg(`❌ ${err.message}`)
    }
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      setRosterText(String(reader.result || ''))
      setImportMsg('📄 File loaded — review below, then click IMPORT')
    }
    reader.onerror = () => setImportMsg('❌ Could not read that file')
    reader.readAsText(file)
    e.target.value = '' // allow re-selecting the same file
  }

  function downloadRosterTemplate() {
    const csv = 'display_name,grade\nAlex Rivera,8\nJordan Kim,8\nSam Patel,8\n'
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url
    a.download = 'roster_template.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const fieldStyle = {
    background: '#1e1b4b', border: '1px solid #4338ca', color: '#e0e7ff',
    fontFamily: 'system-ui', fontSize: '13px', borderRadius: 4,
    padding: '6px 10px', outline: 'none',
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '12px', color: '#a78bfa' }}>
          👥 Classes & Roster
        </h2>
        <button onClick={() => setShowNewClass(v => !v)}
          style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px',
                   background: '#4f46e5', color: '#ffffff', border: '2px solid #818cf8',
                   padding: '6px 14px', borderRadius: 4, cursor: 'pointer' }}>
          + NEW CLASS
        </button>
      </div>

      {error && (
        <div className="mb-6 p-3 rounded-lg" style={{ background: '#3f1d1d', border: '1px solid #ef4444' }}>
          <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#fca5a5' }}>⚠ {error}</span>
        </div>
      )}

      {/* New class form */}
      {showNewClass && (
        <form onSubmit={createClass} className="mb-6 p-5 rounded-lg space-y-4"
              style={{ background: '#0f0f23', border: '2px solid #312e81' }}>
          <h3 style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '9px', color: '#fbbf24' }}>
            Create New Class
          </h3>
          <div className="flex gap-4 flex-wrap">
            <div>
              <label className="block mb-1" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#818cf8' }}>CLASS NAME *</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} required
                placeholder="Period 2 Social Studies" style={{ ...fieldStyle, minWidth: 220 }} />
            </div>
            <div>
              <label className="block mb-1" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#818cf8' }}>GRADE *</label>
              <select value={newGrade} onChange={e => setNewGrade(Number(e.target.value))} style={fieldStyle}>
                <option value={6}>6</option><option value={7}>7</option><option value={8}>8</option>
              </select>
            </div>
            <div>
              <label className="block mb-1" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#818cf8' }}>SECTION</label>
              <input value={newSection} onChange={e => setNewSection(e.target.value)}
                placeholder="2nd Period" style={{ ...fieldStyle, minWidth: 140 }} />
            </div>
            <div>
              <label className="block mb-1" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#818cf8' }}>SESSION LABEL</label>
              <input value={newSession} onChange={e => setNewSession(e.target.value)}
                placeholder="pre_2026" style={{ ...fieldStyle, minWidth: 120 }} />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit"
              style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px',
                       background: '#4f46e5', color: '#fff', border: '2px solid #818cf8',
                       padding: '6px 16px', borderRadius: 4, cursor: 'pointer' }}>
              CREATE
            </button>
            <button type="button" onClick={() => setShowNewClass(false)}
              style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px',
                       background: '#1e1b4b', border: '1px solid #4338ca',
                       color: '#818cf8', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}>
              CANCEL
            </button>
          </div>
        </form>
      )}

      {/* Class list */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {classList.map(cls => (
          <div key={cls.id}
            className="p-4 rounded-lg cursor-pointer transition-all"
            onClick={() => setSelectedClass(cls.id === selectedClass ? null : cls.id)}
            style={{
              background: cls.id === selectedClass ? '#1e1b4b' : '#0f0f23',
              border: `2px solid ${cls.id === selectedClass ? '#6366f1' : '#1e1b4b'}`,
            }}>
            <div className="flex justify-between items-start">
              <div>
                <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '9px', color: '#e0e7ff' }}>
                  {cls.name}
                </p>
                <p style={{ fontFamily: 'system-ui', fontSize: '12px', color: '#6b7280', marginTop: 4 }}>
                  Grade {cls.grade} · {cls.session_label}
                </p>
              </div>
              <div className="text-right">
                <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#a78bfa' }}>
                  {(cls as any).student_count} students
                </p>
                <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px',
                             color: '#34d399', marginTop: 4, letterSpacing: '0.1em' }}>
                  CODE: {(cls as any).class_code}
                  {cls.roster_locked ? <span style={{ color: '#fbbf24', marginLeft: 8 }} title="Only names on the roster can log in">🔒</span> : null}
                </p>
                <button
                  type="button"
                  onClick={e => copyJoinLink(e, cls as any)}
                  title={joinLink((cls as any).class_code)}
                  aria-label={`Copy join link for ${cls.name}`}
                  style={{
                    fontFamily: '"Press Start 2P", monospace', fontSize: '6px', marginTop: 6,
                    padding: '4px 8px', cursor: 'pointer', letterSpacing: '0.05em',
                    background: copiedId === cls.id ? '#065f46' : '#1e1b4b',
                    color: copiedId === cls.id ? '#a7f3d0' : '#818cf8',
                    border: `1px solid ${copiedId === cls.id ? '#34d399' : '#312e81'}`,
                    borderRadius: 4,
                  }}>
                  {copiedId === cls.id ? '✓ COPIED' : '📋 COPY LINK'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Selected class detail */}
      {selectedClass && (
        <div>
          <h3 style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '9px', color: '#fbbf24', marginBottom: 16 }}>
            Student Roster
          </h3>

          {/* Roster lock */}
          {(() => {
            const cls = classList.find(c => c.id === selectedClass)
            if (!cls) return null
            const locked = !!cls.roster_locked
            return (
              <div className="mb-6 p-4 rounded-lg flex items-center justify-between gap-4"
                   style={{ background: '#0f0f23', border: `1px solid ${locked ? '#b45309' : '#1e1b4b'}` }}>
                <div>
                  <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: locked ? '#fbbf24' : '#818cf8', marginBottom: 6 }}>
                    {locked ? '🔒 ROSTER LOCKED' : '🔓 ROSTER OPEN'}
                  </p>
                  <p style={{ fontFamily: 'system-ui', fontSize: '12px', color: '#9ca3af', lineHeight: 1.4 }}>
                    {locked
                      ? 'Only names already on this roster can log in. New names are turned away with "ask your teacher".'
                      : 'Anyone with the class code can log in with any name, which creates a new student. Import your roster, then lock it.'}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={lockBusy}
                  onClick={() => toggleRosterLock(cls)}
                  style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', whiteSpace: 'nowrap',
                           padding: '8px 12px', cursor: lockBusy ? 'wait' : 'pointer', borderRadius: 4,
                           background: locked ? '#1e1b4b' : '#b45309', color: '#fff',
                           border: `2px solid ${locked ? '#312e81' : '#fbbf24'}` }}>
                  {locked ? 'UNLOCK' : 'LOCK ROSTER'}
                </button>
              </div>
            )
          })()}

          {/* Bulk import */}
          <div className="mb-6 p-4 rounded-lg" style={{ background: '#0f0f23', border: '1px solid #1e1b4b' }}>
            <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#818cf8', marginBottom: 8 }}>
              IMPORT ROSTER (paste names, or upload a CSV — one name per line)
            </p>
            <textarea
              value={rosterText}
              onChange={e => setRosterText(e.target.value)}
              placeholder={"Alex Rivera,8\nJordan Kim,8\nSam Patel,8"}
              rows={4}
              style={{ ...fieldStyle, width: '100%', resize: 'vertical', marginBottom: 8 }}
            />
            <div className="flex gap-3 items-center">
              <button onClick={handleBulkImport}
                style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px',
                         background: '#4f46e5', color: '#fff', border: '2px solid #818cf8',
                         padding: '5px 12px', borderRadius: 4, cursor: 'pointer' }}>
                IMPORT
              </button>
              <label
                style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px',
                         background: '#1e1b4b', border: '1px solid #4338ca',
                         color: '#818cf8', padding: '5px 12px', borderRadius: 4, cursor: 'pointer' }}>
                ↑ UPLOAD CSV
                <input type="file" accept=".csv,text/csv,text/plain" onChange={handleFileUpload}
                       style={{ display: 'none' }} />
              </label>
              <button onClick={downloadRosterTemplate}
                style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px',
                         background: '#1e1b4b', border: '1px solid #4338ca',
                         color: '#818cf8', padding: '5px 12px', borderRadius: 4, cursor: 'pointer' }}>
                ↓ TEMPLATE
              </button>
              {importMsg && <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px',
                                            color: importMsg.startsWith('❌') ? '#fca5a5' : '#34d399' }}>{importMsg}</span>}
            </div>
          </div>

          {/* Student table */}
          {loading ? (
            <p style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#6b7280' }}>Loading…</p>
          ) : (
            <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid #1e1b4b' }}>
              <table className="w-full">
                <thead>
                  <tr style={{ background: '#0f0f23', borderBottom: '2px solid #1e1b4b' }}>
                    {ROSTER_COLUMNS.map(c => (
                      <SortTh key={c.key} label={c.label} className="text-left px-4 py-2"
                        active={rosterSort.key === c.key} dir={rosterSort.dir}
                        onClick={() => rosterSort.toggle(c.key, c.first)}
                        style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '7px', color: '#818cf8' }} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedStudents.map((s, i) => (
                    <tr key={s.id} style={{
                      background: i % 2 === 0 ? 'transparent' : '#0a0a1a',
                      borderBottom: '1px solid #1e1b4b',
                    }}>
                      <td className="px-4 py-2" style={{ fontFamily: 'system-ui', fontSize: '13px', color: '#e0e7ff' }}>
                        {s.display_name}
                      </td>
                      <td className="px-4 py-2" style={{ fontFamily: 'monospace', fontSize: '11px', color: '#6b7280' }}>
                        {s.student_code}
                      </td>
                      <td className="px-4 py-2" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#818cf8', textAlign: 'center' }}>
                        {s.grade}
                      </td>
                      <td className="px-4 py-2" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#fbbf24', textAlign: 'center' }}>
                        {s.xp}
                      </td>
                      <td className="px-4 py-2" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#a78bfa', textAlign: 'center' }}>
                        {s.level}
                      </td>
                      <td className="px-4 py-2" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#94a3b8', textAlign: 'center' }}>
                        {s.attempt_count}
                      </td>
                      <td className="px-4 py-2" style={{ fontFamily: 'system-ui', fontSize: '11px', color: '#6b7280' }}>
                        {s.last_active ? (() => {
                          const d = new Date(s.last_active * 1000)
                          const diffMs = Date.now() - d.getTime()
                          const diffMin = Math.floor(diffMs / 60000)
                          const diffHr  = Math.floor(diffMs / 3600000)
                          const diffDay = Math.floor(diffMs / 86400000)
                          const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          const dateStr = d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
                          let relative = ''
                          if (diffMin < 1)        relative = 'Just now'
                          else if (diffMin < 60)  relative = `${diffMin}m ago`
                          else if (diffHr  < 24)  relative = `${diffHr}h ago`
                          else if (diffDay < 7)   relative = `${diffDay}d ago`
                          else                    relative = dateStr
                          const tooltip = `${dateStr} at ${timeStr}`
                          return (
                            <span title={tooltip} style={{ cursor: 'default', borderBottom: '1px dotted #4b5563' }}>
                              {relative}
                              {diffDay < 1 && (
                                <span style={{ display: 'block', fontSize: 10, color: '#4b5563' }}>{timeStr}</span>
                              )}
                            </span>
                          )
                        })() : <span style={{ color: '#4b5563' }}>Never</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {students.length === 0 && (
                <p className="text-center py-6" style={{ fontFamily: '"Press Start 2P", monospace', fontSize: '8px', color: '#6b7280' }}>
                  No students yet. Share the class code with students to get started.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
